"use client";

import type { ActiveBanner } from "@/lib/banners/types";
import { useCurrentUser } from "@/hooks/use-current-user";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

const DISMISSED_BANNERS_KEY = "venturu-dismissed-banners";

function getDismissedBannersStorageKey(userId?: number | string | null): string {
  return `${DISMISSED_BANNERS_KEY}:${userId ?? "anonymous"}`;
}

function getDismissedBannerIds(userId?: number | string | null): number[] {
  try {
    const raw = localStorage.getItem(getDismissedBannersStorageKey(userId));
    if (!raw) return [];
    return JSON.parse(raw) as number[];
  } catch {
    return [];
  }
}

function dismissBanner(id: number, userId?: number | string | null) {
  const dismissed = getDismissedBannerIds(userId);
  if (!dismissed.includes(id)) {
    dismissed.push(id);
    localStorage.setItem(
      getDismissedBannersStorageKey(userId),
      JSON.stringify(dismissed),
    );
  }
}

function matchesAudience(
  banner: ActiveBanner,
  isAgent: boolean,
): boolean {
  if (banner.targetAudience === "all") return true;
  if (banner.targetAudience === "agents") return isAgent;
  if (banner.targetAudience === "buyers_sellers") {
    // Keep audience buckets mutually exclusive:
    // agents -> isAgent users only
    // buyers_sellers -> everyone else (including logged-out users)
    return !isAgent;
  }
  return true;
}

export function InAppBannerContainer() {
  const { user } = useCurrentUser();
  const [banners, setBanners] = useState<ActiveBanner[]>([]);
  const [dismissedIds, setDismissedIds] = useState<number[]>([]);
  const [mounted, setMounted] = useState(false);
  const userId = user?.id ?? null;

  useEffect(() => {
    const controller = new AbortController();

    fetch("/api/banners", {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Banner request failed: ${response.status}`);
        return response.json() as Promise<{ banners?: ActiveBanner[] }>;
      })
      .then((payload) => {
        setBanners(Array.isArray(payload.banners) ? payload.banners : []);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          console.error("[banners] client fetch failed:", error);
          setBanners([]);
        }
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    setDismissedIds(getDismissedBannerIds(userId));
    setMounted(true);
  }, [userId]);

  if (!mounted || banners.length === 0) return null;

  const isAgent = user?.isAgent ?? false;

  const banner = banners.find(
    (b) =>
      !dismissedIds.includes(b.id) && matchesAudience(b, isAgent),
  );

  if (!banner) return null;

  return (
    <InAppBanner
      banner={banner}
      onDismiss={() => {
        dismissBanner(banner.id, userId);
        setDismissedIds((prev) => [...prev, banner.id]);
      }}
    />
  );
}

type BannerStyle = "default" | "success" | "warning" | "info";

const styleConfig: Record<
  BannerStyle,
  { gradient: string; border: string; text: string; cta: string; dismiss: string; dismissHover: string }
> = {
  default: {
    gradient: "from-gray-900 via-gray-800 to-gray-900",
    border: "border-gray-700",
    text: "text-white",
    cta: "text-white underline decoration-white/50",
    dismiss:
      "border-white/20 bg-card/10 text-white/60 hover:text-white hover:bg-card/20",
    dismissHover: "focus-visible:ring-white/40",
  },
  success: {
    gradient: "from-emerald-600 via-emerald-500 to-teal-500",
    border: "border-emerald-400/50",
    text: "text-white",
    cta: "text-white underline decoration-white/50",
    dismiss:
      "border-white/20 bg-card/10 text-white/60 hover:text-white hover:bg-card/20",
    dismissHover: "focus-visible:ring-white/40",
  },
  warning: {
    gradient: "from-amber-500 via-amber-400 to-orange-400",
    border: "border-amber-300/50",
    text: "text-gray-950",
    cta: "text-gray-950 underline decoration-gray-950/40",
    dismiss:
      "border-gray-950/15 bg-gray-950/5 text-gray-950/50 hover:text-gray-950 hover:bg-gray-950/10",
    dismissHover: "focus-visible:ring-gray-950/30",
  },
  info: {
    gradient: "from-blue-600 via-blue-500 to-indigo-500",
    border: "border-blue-400/50",
    text: "text-white",
    cta: "text-white underline decoration-white/50",
    dismiss:
      "border-white/20 bg-card/10 text-white/60 hover:text-white hover:bg-card/20",
    dismissHover: "focus-visible:ring-white/40",
  },
};

function InAppBanner({
  banner,
  onDismiss,
}: {
  banner: ActiveBanner;
  onDismiss: () => void;
}) {
  const config =
    styleConfig[banner.style as BannerStyle] ?? styleConfig.default;
  const href = banner.ctaUrl || undefined;

  return (
    <>
      <style>{`
        :root { --banner-height: 40px; }
        header[class*="fixed"] { top: var(--banner-height) !important; }
      `}</style>
      <div
        className={cn(
          "fixed inset-x-0 top-0 z-[1001] border-b",
          "bg-gradient-to-r shadow-sm",
          config.gradient,
          config.border,
        )}
        style={{ height: "var(--banner-height)" }}
      >
        <div className="container relative mx-auto flex h-full items-center gap-2 pl-4 pr-5 md:justify-center md:px-3">
          {href ? (
            <a
              href={href}
              target={href.startsWith("http") ? "_blank" : undefined}
              rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
              className="flex min-w-0 flex-1 items-center transition-opacity hover:opacity-90 md:max-w-[calc(100%-2.5rem)] md:flex-none md:justify-center"
            >
              <p
                className={cn(
                  "truncate text-left text-[11px] font-semibold tracking-wide md:max-w-full md:text-center md:text-[13px]",
                  config.text,
                )}
              >
                {banner.message}
                {banner.ctaText && (
                  <span className={cn("ml-1.5 underline-offset-[3px] md:ml-2", config.cta)}>
                    {banner.ctaText} →
                  </span>
                )}
              </p>
            </a>
          ) : (
            <div className="flex min-w-0 flex-1 items-center md:max-w-[calc(100%-2.5rem)] md:flex-none md:justify-center">
              <p
                className={cn(
                  "truncate text-left text-[11px] font-semibold tracking-wide md:max-w-full md:text-center md:text-[13px]",
                  config.text,
                )}
              >
                {banner.message}
              </p>
            </div>
          )}

          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDismiss();
            }}
            className={cn(
              "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border md:h-6 md:w-6",
              "md:absolute md:right-3 md:top-1/2 md:-translate-y-1/2",
              "transition-all duration-150",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
              config.dismiss,
              config.dismissHover,
            )}
            aria-label="Dismiss banner"
          >
            <X className="h-2.5 w-2.5 md:h-3 md:w-3" strokeWidth={2.5} />
          </button>
        </div>
      </div>
      <div style={{ height: "var(--banner-height)" }} />
    </>
  );
}
